import { ClientProvinceEnum } from '~types/app-selection';
import { RewardOptionCoupon } from '../../../../stores/loyalty-club';

/**
 * Pure ladder/coupon-grouping logic, deliberately free of React/antd imports:
 * `MilestonesSection/index.tsx` (the picker UI) and the analytics rewards
 * inventory table (RNGR-2381 slice 10) both import this module directly, and
 * neither needs to drag the other's component tree (and its transitive
 * store/context imports) along for a fold or an accumulation.
 */

export type MilestoneOptionForm = {
    /** Client-side key. Options have no server id until the ladder is saved. */
    id: string;
    /** The coupon this row offers, as a `CouponGroup` key. */
    groupKey: string | null;
};

export type MilestoneForm = {
    /** Client-side key, and React's list key across a reorder. */
    id: string;
    visits: number | null;
    options: MilestoneOptionForm[];
};

/**
 * One coupon as the picker offers it.
 *
 * A coupon is stocked per region, so what a human calls one coupon is several
 * promotions sharing a name and a UPC. The operator picks the coupon; this
 * carries the promotions that choice writes to the ladder.
 */
export type CouponGroup = {
    /** Its promotions, so a saved ladder folds back onto the right entry. */
    key: string;
    label: string;
    promotionIds: number[];
};

let keySeed = 0;
function nextKey(prefix: string): string {
    keySeed += 1;
    return `${prefix}-${keySeed}`;
}

export function createOption(): MilestoneOptionForm {
    return { id: nextKey('option'), groupKey: null };
}

export function createMilestone(): MilestoneForm {
    return {
        id: nextKey('milestone'),
        visits: null,
        options: [createOption()],
    };
}

function groupKeyFor(promotionIds: number[]): string {
    return [...promotionIds].sort((a, b) => a - b).join('-');
}

/**
 * Accumulates each tier's `requiredPurchases` delta into its cumulative visit
 * threshold, in position order, mirroring `cumulativeThresholds` in
 * ctd-web-api. Ordering by id instead would drift from the API's own rungs the
 * first time a ladder is reordered or a tier inserted, and the analytics panels
 * key their rows off these numbers.
 */
export function accumulateTierThresholds<
    T extends { position: number; requiredPurchases?: number | null }
>(tierRewards: T[]): { tier: T; visits: number }[] {
    let running = 0;
    return [...tierRewards]
        .sort((a, b) => a.position - b.position)
        .map((tier) => {
            running += tier.requiredPurchases ?? 0;
            return { tier, visits: running };
        });
}

/**
 * The rungs the analytics axes are keyed on: `accumulateTierThresholds` minus
 * the tiers that unlock at no milestone of their own. A tier requiring zero
 * further purchases lands on the tier below's threshold, and the API leaves it
 * out of its `visitMilestone` axis (`milestoneThresholds`), so a panel that
 * kept it would look up rows under a key the API never emits. The editor keeps
 * using the full accumulation, since it draws those tiers too.
 */
export function accumulateMilestoneThresholds<
    T extends { position: number; requiredPurchases?: number | null }
>(tierRewards: T[]): { tier: T; visits: number }[] {
    return accumulateTierThresholds(tierRewards).filter(
        ({ tier }) => (tier.requiredPurchases ?? 0) !== 0
    );
}

function couponName(coupon: RewardOptionCoupon): string {
    return coupon.name ?? `Coupon ${coupon.id}`;
}

/**
 * The regions the campaign allocates stock by, mirroring `ContestRegion` in
 * ctd-web-api (`helpers/provinceHelpers.ts`).
 *
 * A promotion stores its reach as provinces, so that is what the CMS endpoint
 * returns, but nobody curating this screen thinks in provinces: stock is planned
 * per region, so the picker says region. The API stays authoritative for anything
 * behavioural; this is naming only.
 */
enum CouponRegion {
    QUEBEC = 'quebec',
    CENTRAL = 'central',
    WESTERN = 'western',
    ATLANTIC = 'atlantic',
}

const REGION_BY_PROVINCE: Record<ClientProvinceEnum, CouponRegion> = {
    [ClientProvinceEnum.AB]: CouponRegion.WESTERN,
    [ClientProvinceEnum.BC]: CouponRegion.WESTERN,
    [ClientProvinceEnum.MB]: CouponRegion.WESTERN,
    [ClientProvinceEnum.SK]: CouponRegion.WESTERN,
    [ClientProvinceEnum.YT]: CouponRegion.WESTERN,
    [ClientProvinceEnum.NT]: CouponRegion.WESTERN,
    [ClientProvinceEnum.NU]: CouponRegion.WESTERN,
    [ClientProvinceEnum.ON]: CouponRegion.CENTRAL,
    [ClientProvinceEnum.QC]: CouponRegion.QUEBEC,
    [ClientProvinceEnum.NB]: CouponRegion.ATLANTIC,
    [ClientProvinceEnum.NS]: CouponRegion.ATLANTIC,
    [ClientProvinceEnum.PE]: CouponRegion.ATLANTIC,
    [ClientProvinceEnum.NL]: CouponRegion.ATLANTIC,
};

/**
 * Display order and wording both follow the client's spreadsheet columns, which
 * is where a curator reads these numbers from. The platform calls Ontario's
 * region CENTRAL; the sheet calls it Ontario, so the screen does too.
 */
const REGION_LABELS: Record<CouponRegion, string> = {
    [CouponRegion.QUEBEC]: 'Quebec',
    [CouponRegion.CENTRAL]: 'Ontario',
    [CouponRegion.WESTERN]: 'Western',
    [CouponRegion.ATLANTIC]: 'Atlantic',
};

const REGION_ORDER: CouponRegion[] = [
    CouponRegion.QUEBEC,
    CouponRegion.CENTRAL,
    CouponRegion.WESTERN,
    CouponRegion.ATLANTIC,
];

function resolveCouponRegion(code: string): CouponRegion | undefined {
    return REGION_BY_PROVINCE[code.toUpperCase() as ClientProvinceEnum];
}

/** A coupon's reach as regions, always in REGION_ORDER so it reads the same way. */
function regionsOf(provinces: string[]): CouponRegion[] {
    const covered = new Set(provinces.map(resolveCouponRegion).filter(Boolean));

    return REGION_ORDER.filter((region) => covered.has(region));
}

function totalQuantity(set: RewardOptionCoupon[]): string {
    if (set.some((coupon) => coupon.quantity === null)) {
        return 'unlimited';
    }

    return set
        .reduce((total, coupon) => total + (coupon.quantity ?? 0), 0)
        .toLocaleString();
}

/**
 * Splits one UPC's promotions into sets that each cover a province at most once.
 *
 * Normally there is exactly one set, because a coupon has one copy per region. A
 * coupon the campaign offers at two milestones has one set per milestone, since
 * each milestone is stocked separately. FREE Premium Carwash is the only one.
 */
function toAllocationSets(
    bucket: RewardOptionCoupon[]
): RewardOptionCoupon[][] {
    const sets: RewardOptionCoupon[][] = [];

    for (const coupon of [...bucket].sort((a, b) => a.id - b.id)) {
        const free = sets.find((set) =>
            set.every((member) =>
                member.provinceCodes.every(
                    (code) => !coupon.provinceCodes.includes(code)
                )
            )
        );

        if (free) {
            free.push(coupon);
        } else {
            sets.push([coupon]);
        }
    }

    return sets;
}

/**
 * Turns the coupon catalogue into what the picker offers: one entry per coupon
 * instead of one per regional copy, so a milestone is curated by choosing a
 * coupon once rather than four times.
 *
 * The region is named only when the coupon does not reach all four, since that is
 * the case where the name alone hides who gets nothing.
 */
export function buildCouponGroups(
    coupons: RewardOptionCoupon[]
): CouponGroup[] {
    const byActivationCode = new Map<string, RewardOptionCoupon[]>();
    for (const coupon of coupons) {
        // No UPC leaves nothing to group on, so the coupon stands on its own.
        const code = coupon.activationCode ?? `id-${coupon.id}`;
        const bucket = byActivationCode.get(code);
        if (bucket) {
            bucket.push(coupon);
        } else {
            byActivationCode.set(code, [coupon]);
        }
    }

    const groups: CouponGroup[] = [];

    for (const bucket of byActivationCode.values()) {
        const sets = toAllocationSets(bucket);
        // Two sets mean the same coupon is stocked twice, so its name cannot
        // tell the entries apart. Keep what does: the region and the total.
        const isStockedTwice = sets.length > 1;

        for (const set of sets) {
            const regions = regionsOf(
                set.flatMap((coupon) => coupon.provinceCodes)
            );
            const parts = [couponName(set[0])];

            const restricted = set.filter((coupon) => coupon.isForFuelTester);
            if (restricted.length === set.length) {
                parts.push('fuel tester only');
            } else if (restricted.length > 0) {
                const regionNames = regionsOf(
                    restricted.flatMap((coupon) => coupon.provinceCodes)
                ).map((region) => REGION_LABELS[region]);
                parts.push(`fuel tester only in ${regionNames.join('/')}`);
            }
            if (isStockedTwice || regions.length < REGION_ORDER.length) {
                parts.push(
                    regions.map((region) => REGION_LABELS[region]).join('/')
                );
            }
            if (isStockedTwice) {
                parts.push(`${totalQuantity(set)} total`);
            }

            groups.push({
                key: groupKeyFor(set.map((coupon) => coupon.id)),
                label: parts.join(' — '),
                promotionIds: set.map((coupon) => coupon.id),
            });
        }
    }

    return groups.sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * The picker rows for an already-saved ladder, folding a tier's promotions back
 * into the coupons they came from.
 */
export function collapseToOptions(
    promotionIds: number[],
    groups: CouponGroup[]
): MilestoneOptionForm[] {
    const keyByPromotionId = new Map<number, string>();
    for (const group of groups) {
        for (const promotionId of group.promotionIds) {
            keyByPromotionId.set(promotionId, group.key);
        }
    }

    const taken = new Set<string>();
    const options: MilestoneOptionForm[] = [];

    for (const promotionId of promotionIds) {
        const key = keyByPromotionId.get(promotionId);
        if (!key || taken.has(key)) {
            continue;
        }
        taken.add(key);
        options.push({ id: nextKey('option'), groupKey: key });
    }

    return options;
}
